import * as Sentry from '@sentry/browser';
import axios from 'axios';
import { isNil, find, assign, isArray, toUpper, toLower, remove } from 'lodash';
import { observable, action, makeObservable } from 'mobx';

import epaxios from '@/utils/epaxios';
import LogErrorMessage from '@/utils/LogErrorMessage';

import RootStore from './RootStore';

export default class RulesStore {
  rootStore: typeof RootStore;
  @observable rules = [];
  @observable isLoading = false;
  @observable getByIdCancelr;

  constructor(rootStore) {
    makeObservable(this);
    this.rootStore = rootStore;
  }

  @action get = async () => {
    this.isLoading = true;

    try {
      const { data: rules } = await epaxios.get(`/alert-rule/station/${this.rootStore.stationStore.currentStation.id}`);

      // only assign alerts if its an array.
      // disregard 403 errors, where response is an object(e.g. {"status":403,"error":"Forbidden","message":"Forbidden resource"})
      isArray(rules)
        ? (this.rules = rules.map(rule => {
            const r = { ...rule };
            r.alertType = toUpper(r.alertType);
            return r;
          }))
        : (this.rules = []);
    } catch (e) {
      this.rules = [];
    } finally {
      this.isLoading = false;
    }
  };

  @action getById = async (alertRuleId, alertType) => {
    this.isLoading = true;
    try {
      if (!alertRuleId) return;
      // Get a single alert rule
      const { data: newAlertRule } = await epaxios.get(
        `/${alertType}-alert-rule/station/${this.rootStore.stationStore.currentStation.id}/${alertRuleId}`,
        {
          cancelToken: new axios.CancelToken(c => {
            this.getByIdCancelr = c;
          }),
        },
      );
      newAlertRule.alertType = newAlertRule.alertType.toUpperCase();

      const oldAlertRule = find(this.rules, { id: alertRuleId });
      oldAlertRule
        ? (this.rules = this.rules.map(r => (r.id === alertRuleId ? newAlertRule : r)))
        : this.rules.push(newAlertRule);

      return find(this.rules, { id: alertRuleId });
    } catch (e) {
      Sentry.captureException(new Error(`Error when getting alert: ${e}`));
    } finally {
      this.isLoading = false;
    }
  };

  @action update = async (payload, alertType, ruleId) => {
    const alertTypeLowered = toLower(alertType);

    try {
      const { data } = await epaxios.put(
        `/${alertTypeLowered}-alert-rule/station/${this.rootStore.stationStore.currentStation.id}/${ruleId}`,
        payload,
      );

      if (isNil(data)) {
        Sentry.captureMessage('Did not get response from alert rule update');
        return Promise.reject();
      }

      const oldAlertRule = find(this.rules, { id: data.id });
      assign(oldAlertRule, data);
    } catch (e) {
      LogErrorMessage('Custom alert rule creation failed ', e);
      return Promise.reject(e);
    }
  };

  @action delete = async alertRule => {
    try {
      const alertTypeLoweredCase = toLower(alertRule.alertType);
      await epaxios.delete(
        `/${alertTypeLoweredCase}-alert-rule/station/${this.rootStore.stationStore.currentStation.id}/${alertRule.id}`,
      );

      remove(this.rules, r => r.id === alertRule.id);
      this.rules = [...this.rules];
    } catch (e) {
      LogErrorMessage('Failed to delete an alert rule ', e);
      return Promise.reject(e);
    }
  };

  @action create = async (payload, alertType) => {
    try {
      await epaxios.post(`/${alertType}-alert-rule/station/${this.rootStore.stationStore.currentStation.id}/`, payload);
    } catch (e) {
      LogErrorMessage('Custom alert rule creation failed ', e);
      return Promise.reject(e);
    }
  };

  @action revert = async ruleId => {
    try {
      const { data } = await epaxios.put(
        `/manufacturer-alert-rule/station/${this.rootStore.stationStore.currentStation.id}/${ruleId}/revert_to_default`,
      );
      return data;
    } catch (e) {
      LogErrorMessage('Failed to revert rule ', e);
      return Promise.reject(e);
    }
  };

  cancelGetById = () => {
    this.getByIdCancelr && this.getByIdCancelr();
  };
}
